import { create } from 'zustand';

// Tab disponibili nel pannello Impostazioni
export type SettingsTab = 'rules' | 'tags' | 'blocklist' | 'usage' | 'aikey' | 'extension';

// Viste principali dell'app (navigazione senza react-router)
export type AppView =
  | 'list'
  | 'dashboard'
  | 'feed'
  | 'search'
  | 'folders'
  | 'kpi'
  | 'contacts'
  | 'saved'
  | 'changelog'
  | 'manuali'
  | 'linkedin'
  | 'deletion'
  | 'sent'
  | 'settings'
  | 'home';

// Tab interni della pagina LinkedIn (Estensione / Salvati / Coda attività)
export type LinkedInTab = 'extension' | 'saved' | 'queue' | 'manual';

// Filtro "in attesa" da applicare alla vista Posta (ListView) alla prossima apertura:
// impostato dal Cruscotto (click su card categoria / chip tag / spam), consumato
// e azzerato da ListView al mount. Per kind='tag' value è l'id del tag.
export type PendingMailFilter = { kind: 'category' | 'tag' | 'spam'; value: string } | null;

// ----- Tema chiaro/scuro -----
export type ThemeMode = 'dark' | 'light';

// Applica il tema al document root: classe `light`/`dark` su <html>.
// Le utility Tailwind restano dark di default; gli override light vivono in index.css.
function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle('light', mode === 'light');
  root.classList.toggle('dark', mode === 'dark');
}

// Legge la preferenza salvata (default: dark).
function initialTheme(): ThemeMode {
  try {
    const saved = localStorage.getItem('wm-theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* localStorage non disponibile: fallback dark */
  }
  return 'dark';
}

// Legge lo stato salvato della sidebar collassata (default: false = espansa).
function initialSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem('wm-sidebar') === 'collapsed';
  } catch {
    /* localStorage non disponibile: fallback espansa */
  }
  return false;
}

// Applica subito il tema iniziale (evita flash al primo render).
const _initialTheme = initialTheme();
applyTheme(_initialTheme);

const _initialSidebarCollapsed = initialSidebarCollapsed();

interface UiState {
  // Tema chiaro/scuro (persistito in localStorage 'wm-theme', default dark)
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;

  // Navigazione a viste (default: Home della piattaforma)
  currentView: AppView;
  setCurrentView: (view: AppView) => void;

  // Tab attivo della pagina LinkedIn (selezionato anche dalle sottovoci sidebar)
  linkedinTab: LinkedInTab;
  setLinkedinTab: (tab: LinkedInTab) => void;
  // Scorciatoia: apre la pagina LinkedIn su un tab specifico
  openLinkedIn: (tab?: LinkedInTab) => void;

  // Filtro in attesa per la vista Posta (impostato dal Cruscotto, consumato da ListView)
  pendingMailFilter: PendingMailFilter;
  setPendingMailFilter: (f: PendingMailFilter) => void;

  // CRM: contatto selezionato nella vista Contatti (usato anche per navigare
  // da SavedItemsView alla scheda del contatto collegato)
  selectedContactId: string | null;
  setSelectedContact: (id: string | null) => void;

  // Sidebar a scomparsa (persistita in localStorage 'wm-sidebar', default espansa)
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;

  settingsOpen: boolean;
  settingsTab: SettingsTab;
  openSettings: (tab?: SettingsTab) => void;
  closeSettings: () => void;
  setSettingsTab: (tab: SettingsTab) => void;

  // Paragrafo/sezione del manuale a cui scrollare alla prossima apertura della
  // vista "Manuali" (es. 'linkedin' per le FAQ dell'Assistente LinkedIn).
  // Opzionale: se null, ManualiView si apre in cima come oggi (retrocompatibile).
  // Viene consumato e azzerato da ManualiView subito dopo lo scroll.
  manualiTarget: string | null;
  setManualiTarget: (target: string | null) => void;
  // Ambito del manuale: 'linkedin' = mostra SOLO il manuale dell'Assistente
  // LinkedIn; null = manuale generale della webmail (esclude le sezioni LinkedIn).
  // Persistente (non azzerato dallo scroll) così ogni sezione ha il SUO manuale.
  manualiScope: 'linkedin' | null;
  setManualiScope: (scope: 'linkedin' | null) => void;
}

// Store UI globale: gestisce la vista corrente e l'apertura del pannello Impostazioni
export const useUiStore = create<UiState>((set, get) => ({
  theme: _initialTheme,
  setTheme: (mode) => {
    applyTheme(mode);
    try {
      localStorage.setItem('wm-theme', mode);
    } catch {
      /* ignora errori di persistenza */
    }
    set({ theme: mode });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  currentView: 'home',
  setCurrentView: (view) => set({ currentView: view }),

  linkedinTab: 'saved',
  setLinkedinTab: (tab) => set({ linkedinTab: tab }),
  openLinkedIn: (tab) =>
    set({ currentView: 'linkedin', ...(tab ? { linkedinTab: tab } : {}) }),

  pendingMailFilter: null,
  setPendingMailFilter: (f) => set({ pendingMailFilter: f }),

  selectedContactId: null,
  setSelectedContact: (id) => set({ selectedContactId: id }),

  sidebarCollapsed: _initialSidebarCollapsed,
  setSidebarCollapsed: (v) => {
    try {
      localStorage.setItem('wm-sidebar', v ? 'collapsed' : 'expanded');
    } catch {
      /* ignora errori di persistenza */
    }
    set({ sidebarCollapsed: v });
  },
  toggleSidebar: () => get().setSidebarCollapsed(!get().sidebarCollapsed),

  settingsOpen: false,
  settingsTab: 'rules',
  openSettings: (tab) => set({ settingsOpen: true, ...(tab ? { settingsTab: tab } : {}) }),
  closeSettings: () => set({ settingsOpen: false }),
  setSettingsTab: (tab) => set({ settingsTab: tab }),

  manualiTarget: null,
  setManualiTarget: (target) => set({ manualiTarget: target }),
  manualiScope: null,
  setManualiScope: (scope) => set({ manualiScope: scope }),
}));
